import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChatCircleDots, Phone, Brain, Lightning, ShieldCheck, Plugs,
  ArrowRight, Books, Headset, Sparkle, Waveform, Star, Ticket, Robot, UserCircle, PaperPlaneRight, Lifebuoy,
  CheckCircle, MagnifyingGlass, Tray, Smiley, ChartBar,
  Plus, Bell, Question, SquaresFour, Gauge, Gear, CaretDown, CaretLeft, CaretRight, Envelope, Clock, User, Funnel,
  FileArrowDown, List, X, Scales, GlobeHemisphereWest, Lock, Eye, ListChecks,
} from "@phosphor-icons/react";
import { SiZendesk } from "react-icons/si";
import { BrochureOrb } from "../components/BrochureOrb";
import Seo from "@/components/Seo";
import { IntroVideo } from "../components/IntroVideo";
import { toast } from "sonner";

const VOICE_LINES = [
  "Hej och varmt välkommen till Senzum! Vad roligt att du hör av dig. Berätta gärna lite mer om vad du behöver hjälp med idag, så löser vi det tillsammans steg för steg.",
  "Jag ser din order #4821 här i systemet. Den är packad och redan på väg, och enligt spårningen levereras den till dig redan före klockan 18.00 ikväll — vill du att jag skickar en spårningslänk?",
  "Absolut, det ordnar jag direkt åt dig. Jag har nu bokat om din tid till imorgon klockan 14.00 och skickar en bekräftelse till din mejl med alla detaljer och en kalenderinbjudan.",
  "Tack för ditt tålamod! Det här vill jag att en av våra specialister tittar närmare på, så jag kopplar dig vidare nu. Oroa dig inte — du behöver inte upprepa något, jag skickar med hela ärendet.",
];

function VoiceShowcase() {
  const [speaking, setSpeaking] = useState(true);
  const [line, setLine] = useState(0);
  useEffect(() => {
    let speakingNow = true;
    let idx = 0;
    let timeout;
    const schedule = () => {
      if (speakingNow) {
        // talk time scales with the sentence length so longer lines are spoken longer
        const dur = Math.min(12000, 4000 + VOICE_LINES[idx].length * 45);
        timeout = setTimeout(() => { speakingNow = false; setSpeaking(false); schedule(); }, dur);
      } else {
        timeout = setTimeout(() => {
          idx = (idx + 1) % VOICE_LINES.length;
          setLine(idx);
          speakingNow = true; setSpeaking(true); schedule();
        }, 2600);
      }
    };
    setSpeaking(true); setLine(0); schedule();
    return () => clearTimeout(timeout);
  }, []);
  return (
    <div className="rounded-xl border border-white/10 bg-zinc-950 p-5 shadow-sm">
      <div className="flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-zinc-400 mb-2">
        <Phone size={16} weight="bold" className="text-[#5C7CFF]" /> AI-Voice Agent · Live
      </div>
      <div className="flex flex-col items-center justify-center">
        <BrochureOrb speaking={speaking} size={248} color={[92, 124, 255]} />
        <div className="flex items-center gap-2 mt-2">
          <span className={`h-2 w-2 rounded-full ${speaking ? "bg-[#0033FF] animate-pulse" : "bg-zinc-300"}`} />
          <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            {speaking ? "Talar" : "Lyssnar"}
          </span>
        </div>
        <p className="mt-3 text-center text-zinc-400 text-sm h-16 leading-snug transition-opacity duration-500 max-w-md overflow-hidden">
          "{VOICE_LINES[line]}"
        </p>
      </div>
    </div>
  );
}

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

function Logo() {
  return (
    <div className="flex items-center">
      <img src="/senzum-logo-white.png" alt="Senzum AI" className="h-8 w-auto" />
    </div>
  );
}

const CONVO = [
  { role: "user", text: "Hej! Min leverans har inte kommit än 😟" },
  { role: "ai", text: "Hej! Jag är ledsen att höra det. Jag ser att order #4821 är ute för leverans och kommer idag före kl 18.00." },
  { role: "user", text: "Skönt! Kan jag få en spårningslänk?" },
  { role: "ai", text: "Självklart — här är din live-spårning. Är det något mer jag kan hjälpa till med?" },
  { role: "user", text: "Perfekt, tack så jättemycket för hjälpen! 🙏" },
  { role: "ai", text: "Så glad att jag kunde hjälpa till! Ha en riktigt fin dag 🌟" },
];

const BRANDS = [
  { name: "Zendesk", icon: <SiZendesk size={18} /> },
  { name: "Freshdesk", icon: <Ticket size={16} weight="bold" className="text-[#25c16f]" /> },
  { name: "Zisson", icon: <Phone size={16} weight="bold" /> },
  { name: "Genesys", icon: <Headset size={16} weight="bold" /> },
  { name: "Dixa", icon: <ChatCircleDots size={16} weight="bold" className="text-[#5C7CFF]" /> },
  { name: "Kundo", icon: <Lifebuoy size={16} weight="bold" className="text-[#F97316]" /> },
];

function AnimatedConversation() {
  const [visible, setVisible] = useState([]);
  const [typingRole, setTypingRole] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const timers = [];
    const wait = (ms) => new Promise((res) => { const t = setTimeout(res, ms); timers.push(t); });

    const run = async () => {
      await wait(1200);
      while (!cancelled) {
        setVisible([]); setTypingRole(null); setDraft(""); setSending(false);
        await wait(900);
        for (let i = 0; i < CONVO.length && !cancelled; i++) {
          const m = CONVO[i];
          if (m.role === "user") {
            // type the customer's message into the input box, char by char, then send
            for (let c = 1; c <= m.text.length && !cancelled; c++) {
              setDraft(m.text.slice(0, c));
              await wait(26 + Math.random() * 34);
            }
            if (cancelled) break;
            await wait(450);
            setSending(true);
            await wait(180);
            setDraft("");
            setSending(false);
            setVisible((v) => [...v, m]);
            await wait(1000);
          } else {
            setTypingRole("ai");
            await wait(Math.min(3000, 1000 + m.text.length * 22));
            if (cancelled) break;
            setTypingRole(null);
            setVisible((v) => [...v, m]);
            await wait(Math.min(3000, 1200 + m.text.length * 16));
          }
        }
        await wait(2600);
      }
    };
    run();
    return () => { cancelled = true; timers.forEach(clearTimeout); };
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [visible, typingRole, draft]);

  return (
    <div className="rounded-2xl border border-white/10 bg-zinc-950/5 backdrop-blur p-5 h-[520px] flex flex-col">
      <div className="flex items-center gap-2 pb-4 border-b border-white/10">
        <div className="h-9 w-9 rounded-full bg-[#0033FF] flex items-center justify-center"><Robot size={18} weight="fill" className="text-white" /></div>
        <div>
          <div className="text-white font-semibold text-sm">Senzum-agent</div>
          <div className="text-[11px] text-green-400 flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-green-400" /> Online</div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-hidden flex flex-col gap-2.5 py-4">
        {visible.map((m, i) => (
          <motion.div key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "ai" && <div className="h-6 w-6 rounded-full bg-[#0033FF]/30 flex items-center justify-center shrink-0"><Robot size={13} weight="fill" className="text-[#5C7CFF]" /></div>}
            <div className={`w-[78%] text-sm px-3.5 py-2 rounded-2xl ${m.role === "user" ? "rounded-br-sm bg-[#0033FF] text-white" : "rounded-bl-sm bg-white/5 text-zinc-100"}`}>
              {m.text}
            </div>
            {m.role === "user" && <div className="h-6 w-6 rounded-full bg-white/10 flex items-center justify-center shrink-0"><UserCircle size={15} weight="fill" className="text-zinc-300" /></div>}
          </motion.div>
        ))}
        {typingRole === "ai" && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
            className="flex items-end gap-2 justify-start">
            <div className="h-6 w-6 rounded-full bg-[#0033FF]/30 flex items-center justify-center shrink-0"><Robot size={13} weight="fill" className="text-[#5C7CFF]" /></div>
            <div className="w-[78%] px-4 py-3 rounded-2xl rounded-bl-sm bg-white/5">
              <span className="inline-flex gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-zinc-400 typing-dot" />
                <span className="h-1.5 w-1.5 rounded-full bg-zinc-400 typing-dot" style={{ animationDelay: "0.2s" }} />
                <span className="h-1.5 w-1.5 rounded-full bg-zinc-400 typing-dot" style={{ animationDelay: "0.4s" }} />
              </span>
            </div>
          </motion.div>
        )}
      </div>

      <div className="pt-3 border-t border-white/10 flex items-center gap-2">
        <div className="flex-1 rounded-full bg-white/5 border border-white/10 px-4 py-2.5 text-sm min-h-[42px] flex items-center">
          {draft ? (
            <span className="text-white">{draft}<span className="inline-block w-[2px] h-4 bg-[#5C7CFF] ml-0.5 align-middle animate-pulse" /></span>
          ) : (
            <span className="text-zinc-500">Skriv ett meddelande…</span>
          )}
        </div>
        <motion.div
          animate={sending ? { scale: [1, 0.85, 1] } : {}}
          transition={{ duration: 0.25 }}
          className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${draft || sending ? "bg-[#0033FF] text-white" : "bg-white/10 text-zinc-400"}`}>
          <PaperPlaneRight size={16} weight="fill" />
        </motion.div>
      </div>
    </div>
  );
}

const CHAT_PREVIEW = [
  { role: "user", text: "Hej, jag vill lämna en reklamation. Mina nya hörlurar slutade fungera efter en vecka 😡" },
  { role: "ai", text: "Vad tråkigt att höra! Det ska absolut inte hända. Kan du ge mig ditt ordernummer så tittar jag direkt?" },
  { role: "user", text: "Javisst, det är order #4821." },
  { role: "ai", text: "Tack! Jag ser ordern — den har full garanti. Jag skickar ett nytt par utan kostnad och en förbetald returfraktsedel direkt." },
  { role: "user", text: "Oj, vad smidigt! Det uppskattar jag verkligen 😊" },
  { role: "ai", text: "Så ska det vara! Hör av dig om det är något mer. Ha en fin dag!" },
  { role: "user", text: "Tack så mycket, hej då! 👋" },
];

function AnimatedChatPreview() {
  const [visible, setVisible] = useState([]);
  const [typingRole, setTypingRole] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const timers = [];
    const wait = (ms) => new Promise((res) => { const t = setTimeout(res, ms); timers.push(t); });
    const run = async () => {
      await wait(600);
      while (!cancelled) {
        setVisible([]); setTypingRole(null); setDraft(""); setSending(false);
        await wait(600);
        for (let i = 0; i < CHAT_PREVIEW.length && !cancelled; i++) {
          const m = CHAT_PREVIEW[i];
          if (m.role === "user") {
            // type the message into the input box, char by char, then send it
            for (let c = 1; c <= m.text.length && !cancelled; c++) {
              setDraft(m.text.slice(0, c));
              await wait(26 + Math.random() * 34);
            }
            if (cancelled) break;
            await wait(450);
            setSending(true);
            await wait(180);
            setDraft("");
            setSending(false);
            setVisible((v) => [...v, m]);
            await wait(950);
          } else {
            setTypingRole("ai");
            await wait(Math.min(2600, 900 + m.text.length * 22));
            if (cancelled) break;
            setTypingRole(null);
            setVisible((v) => [...v, m]);
            await wait(Math.min(2800, 1100 + m.text.length * 14));
          }
        }
        await wait(2600);
      }
    };
    run();
    return () => { cancelled = true; timers.forEach(clearTimeout); };
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [visible, typingRole, draft]);

  return (
    <div className="flex flex-col h-[320px]">
      <div ref={scrollRef} data-testid="chat-preview-animation" className="flex-1 overflow-hidden flex flex-col gap-2.5 pb-3">
        {visible.map((m, i) => (
          <motion.div key={i}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "ai" && <div className="h-6 w-6 rounded-full bg-[#0033FF]/10 flex items-center justify-center shrink-0"><Robot size={13} weight="fill" className="text-[#5C7CFF]" /></div>}
            <div className={`w-[78%] text-sm px-4 py-2.5 rounded-2xl ${m.role === "user" ? "rounded-br-sm bg-[#0033FF] text-white" : "rounded-bl-sm bg-white/5 text-zinc-100"}`}>
              {m.text}
            </div>
            {m.role === "user" && <div className="h-6 w-6 rounded-full bg-white/10 flex items-center justify-center shrink-0"><UserCircle size={15} weight="fill" className="text-zinc-400" /></div>}
          </motion.div>
        ))}
        {typingRole === "ai" && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
            className="flex items-end gap-2 justify-start">
            <div className="h-6 w-6 rounded-full bg-[#0033FF]/10 flex items-center justify-center shrink-0"><Robot size={13} weight="fill" className="text-[#5C7CFF]" /></div>
            <div className="w-[78%] px-4 py-3 rounded-2xl rounded-bl-sm bg-white/5">
              <span className="inline-flex gap-1">
                <span className="h-1.5 w-1.5 rounded-full typing-dot bg-zinc-400" />
                <span className="h-1.5 w-1.5 rounded-full typing-dot bg-zinc-400" style={{ animationDelay: "0.2s" }} />
                <span className="h-1.5 w-1.5 rounded-full typing-dot bg-zinc-400" style={{ animationDelay: "0.4s" }} />
              </span>
            </div>
          </motion.div>
        )}
      </div>

      {/* write & send bar */}
      <div className="flex items-center gap-2 border-t border-white/5 pt-3">
        <div className="flex-1 rounded-full bg-white/5 px-4 py-2.5 text-sm min-h-[40px] flex items-center">
          {draft ? (
            <span className="text-white">{draft}<span className="inline-block w-[2px] h-4 bg-[#5C7CFF] ml-0.5 align-middle animate-pulse" /></span>
          ) : (
            <span className="text-zinc-400">Skriv ett meddelande…</span>
          )}
        </div>
        <motion.div
          animate={sending ? { scale: [1, 0.85, 1] } : {}}
          transition={{ duration: 0.25 }}
          className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${draft || sending ? "bg-[#0033FF] text-white" : "bg-white/10 text-zinc-400"}`}>
          <PaperPlaneRight size={16} weight="fill" />
        </motion.div>
      </div>
    </div>
  );
}
const AP_AVATARS = [
  { bg: "#DBEAFE", fg: "#2563EB" },
  { bg: "#EDE9FE", fg: "#7C3AED" },
  { bg: "#FCE7F3", fg: "#DB2777" },
  { bg: "#DCFCE7", fg: "#16A34A" },
  { bg: "#FEF3C7", fg: "#D97706" },
  { bg: "#E0E7FF", fg: "#4F46E5" },
];

const AP_PRIO = { "Låg": "#22C55E", "Medel": "#F59E0B", "Hög": "#EF4444" };

const AP_TICKETS = [
  { id: "5821", subj: "Vill returnera ett par skor", cust: "Anna Lindqvist", extra: ["Retur"], replied: "5 min", overdue: "3 dagar", prio: "Låg", agent: "SE / Oskar - AI", focus: true },
  { id: "5822", subj: "Re: Din order är nu mottagen", cust: "Maria Mikko", extra: [], replied: "8 timmar", overdue: "14 timmar", prio: "Låg", agent: "SE / Johan K…" },
  { id: "5823", subj: "Re: Fwd: Återbetalning", cust: "Support Merchant", extra: ["Intern mail"], replied: "18 timmar", overdue: "1 dag", prio: "Låg", agent: "Support / Josefine H…" },
  { id: "5824", subj: "Till Patrycja", cust: "Centsy de Boaventura", extra: ["Order", "Betalning"], replied: "6 timmar", overdue: "2 dagar", prio: "Medel", agent: "SE / Patrycja S…" },
  { id: "5825", subj: "Faktura saknas på order", cust: "Unni Dahlen", extra: ["Betalning", "Order"], replied: "6 timmar", overdue: "7 dagar", prio: "Medel", agent: "NO / Marie S…" },
  { id: "5826", subj: "Produkten kom skadad", cust: "Oskar Falk", extra: ["Reklamation"], replied: "2 timmar", overdue: "5 timmar", prio: "Hög", agent: "SE / Erik H…" },
  { id: "5827", subj: "Vill byta storlek på tröja", cust: "Lina Ek", extra: ["Byte"], replied: "11 timmar", overdue: "7 dagar", prio: "Medel", agent: "NO / Marie S…" },
  { id: "5828", subj: "Hur spårar jag mitt paket?", cust: "Daniel Roos", extra: ["Frakt"], replied: "2 dagar", overdue: "7 dagar", prio: "Låg", agent: "EE / Marleen M…" },
];

const AP_PHASES = [
  "Skannar inkorgen efter nya ärenden…",
  "Öppnar ärende #5821 — returbegäran",
  "Utformar svar från kunskapsbasen…",
  "Skickar svaret till kunden",
  "Stänger ärendet som löst",
  "Klart! Återgår till inkorgen…",
];

function useCountUp(target, duration = 1600) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf; const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      const e = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * e));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

function AutopilotCounters({ extraResolved }) {
  const baseResolved = useCountUp(1247);
  const csat = useCountUp(96);
  const [perHour, setPerHour] = useState(0);
  useEffect(() => {
    const t0 = performance.now(); let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / 1600);
      setPerHour(Math.round(38 * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const id = setInterval(() => setPerHour((v) => Math.max(31, Math.min(46, v + (Math.random() > 0.5 ? 1 : -1)))), 3200);
    return () => { cancelAnimationFrame(raf); clearInterval(id); };
  }, []);
  const resolved = baseResolved + extraResolved;
  const stats = [
    { icon: CheckCircle, value: resolved.toLocaleString("sv-SE"), label: "Ärenden lösta", accent: "#28C840" },
    { icon: Lightning, value: perHour, label: "Ärenden / timme", accent: "#5C7CFF" },
    { icon: Smiley, value: `${csat}%`, label: "CSAT", accent: "#7C3AED" },
  ];
  return (
    <div data-testid="autopilot-counters" className="grid grid-cols-3 gap-3 sm:gap-4 max-w-2xl mx-auto mb-8">
      {stats.map((s) => (
        <div key={s.label} className="rounded-xl border border-white/10 bg-zinc-950/[0.03] px-3 py-4 text-center">
          <div className="flex items-center justify-center gap-1.5">
            <s.icon size={16} weight="fill" style={{ color: s.accent }} />
            <span className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight text-white tabular-nums">{s.value}</span>
          </div>
          <div className="mt-1 text-[11px] font-bold uppercase tracking-wider text-zinc-500">{s.label}</div>
        </div>
      ))}
    </div>
  );
}

function FdTag({ children, kind }) {
  const styles = {
    overdue: "bg-[#FEE2E2] text-[#DC2626]",
    replied: "bg-[#DBEAFE] text-[#2563EB]",
    plain: "bg-zinc-100 text-zinc-500 border border-zinc-200",
  };
  return <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${styles[kind]}`}>{children}</span>;
}

function FreshdeskAutopilot() {
  const [phase, setPhase] = useState(0);
  const [resolved, setResolved] = useState(false);
  const [resolvedCount, setResolvedCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const timers = [];
    const wait = (ms) => new Promise((res) => { const t = setTimeout(res, ms); timers.push(t); });
    const run = async () => {
      while (!cancelled) {
        setResolved(false); setPhase(0); await wait(2800);
        setPhase(1); await wait(2200);
        setPhase(2); await wait(2700);
        setPhase(3); await wait(2400);
        setPhase(4); await wait(1300);
        if (!cancelled) { setResolved(true); setResolvedCount((c) => c + 1); }
        await wait(1500);
        setPhase(5); await wait(2400);
      }
    };
    run();
    return () => { cancelled = true; timers.forEach(clearTimeout); };
  }, []);

  const showList = phase === 0 || phase === 5;
  const focus = AP_TICKETS[0];
  const openCount = AP_TICKETS.length - (phase === 5 ? 1 : 0);

  return (
    <div>
      <AutopilotCounters extraResolved={resolvedCount} />
      <div data-testid="autopilot-simulation" className="rounded-xl overflow-hidden border border-white/10 shadow-2xl shadow-black/40 bg-white text-left">
        {/* browser chrome */}
        <div className="flex items-center gap-2 px-3.5 py-2.5 bg-[#23252B]">
          <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
          <div className="flex-1 flex justify-center">
            <div className="flex items-center gap-1.5 rounded-md bg-white/10 px-3 py-1 text-[11px] text-zinc-300 max-w-[260px] w-full justify-center">
              <ShieldCheck size={11} weight="fill" className="text-[#28C840]" /> senzum.freshdesk.com/a/tickets
            </div>
          </div>
        </div>

        {/* freshdesk top bar */}
        <div className="flex items-center gap-2.5 h-12 px-3 bg-white border-b border-zinc-200">
          <div className="h-8 w-8 rounded-lg bg-[#25C16F] flex items-center justify-center shrink-0">
            <Headset size={16} weight="fill" className="text-white" />
          </div>
          <button className="h-7 w-7 rounded border border-zinc-200 flex items-center justify-center text-zinc-400 shrink-0"><Funnel size={13} weight="bold" /></button>
          <span className="text-[13px] font-semibold text-[#0B1F33] flex items-center gap-1.5">
            Förfallen <span className="bg-[#2B3A4A] text-white text-[10px] font-bold px-1.5 py-0.5 rounded">{openCount}</span>
          </span>
          <div className="ml-auto flex items-center gap-1.5 sm:gap-2.5 text-zinc-500">
            <button className="hidden sm:flex items-center gap-1 text-[12px] font-semibold border border-zinc-200 rounded-md px-2 py-1"><Plus size={12} weight="bold" /> Ny <CaretDown size={10} weight="bold" /></button>
            <button className="hidden md:flex items-center gap-1 text-[12px] font-medium"><MagnifyingGlass size={13} weight="bold" /> Sök</button>
            <Bell size={16} weight="regular" className="hidden sm:block" />
            <div className="relative hidden sm:block"><Question size={16} weight="regular" /><span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-red-500" /></div>
            <button className="hidden lg:flex items-center gap-1 text-[12px] font-medium"><SquaresFour size={14} weight="regular" /> Appar</button>
            <div className="h-7 w-7 rounded-full bg-[#F97316] text-white text-[11px] font-bold flex items-center justify-center shrink-0">S</div>
          </div>
        </div>

        {/* app body */}
        <div className="flex h-[460px]">
          {/* left icon rail */}
          <div className="w-12 bg-white border-r border-zinc-200 flex flex-col items-center py-3 gap-4 shrink-0 text-zinc-400">
            <Gauge size={18} weight="regular" />
            <div className="rounded-lg bg-[#0033FF]/10 p-1.5 text-[#0033FF]"><Tray size={18} weight="fill" /></div>
            <User size={18} weight="regular" />
            <Books size={18} weight="regular" />
            <ChatCircleDots size={18} weight="regular" />
            <ChartBar size={18} weight="regular" />
            <Gear size={18} weight="regular" className="mt-auto" />
          </div>

          {/* views panel */}
          <div className="w-44 border-r border-zinc-200 bg-white p-3 hidden lg:flex flex-col gap-3 shrink-0">
            <div className="flex items-center gap-1.5 rounded-md border border-zinc-200 px-2 py-1.5 text-[11px] text-zinc-400">
              <MagnifyingGlass size={12} weight="bold" /> Sök för en vy
            </div>
            <div className="flex items-center justify-between text-[13px] font-semibold text-[#0B1F33] pt-1">Delad <CaretDown size={12} weight="bold" className="text-zinc-400" /></div>
            <div className="border-t border-zinc-100" />
            <div className="flex items-center justify-between text-[13px] font-semibold text-[#0B1F33]">Standard <CaretDown size={12} weight="bold" className="text-zinc-400" /></div>
          </div>

          {/* main column */}
          <div className="flex-1 min-w-0 flex flex-col bg-[#F5F6F8]">
            {/* list toolbar */}
            <div className="flex items-center gap-3 px-4 h-10 bg-white border-b border-zinc-200 text-[12px] shrink-0">
              <input type="checkbox" disabled readOnly className="h-3.5 w-3.5 rounded border-zinc-300 accent-[#0033FF]" />
              <span className="text-zinc-400">Sortera efter: <b className="text-[#0B1F33] font-semibold">Skapelsedatum</b></span>
              <CaretDown size={11} weight="bold" className="text-zinc-400 -ml-1.5" />
              <div className="ml-auto flex items-center gap-3 text-zinc-400">
                <span className="hidden md:flex items-center gap-1">Layout: <b className="text-[#0B1F33] font-semibold">Kortvy</b> <CaretDown size={11} weight="bold" /></span>
                <span className="hidden sm:inline">1 – {AP_TICKETS.length} av {AP_TICKETS.length}</span>
                <div className="hidden sm:flex border border-zinc-200 rounded-md overflow-hidden">
                  <span className="px-1.5 py-1 border-r border-zinc-200"><CaretLeft size={11} weight="bold" /></span>
                  <span className="px-1.5 py-1"><CaretRight size={11} weight="bold" /></span>
                </div>
                <span className="flex items-center gap-1 font-semibold text-[#0B1F33] border border-zinc-200 rounded-md px-2 py-1"><Funnel size={12} weight="bold" /> Filter (1)</span>
              </div>
            </div>

            {/* AI activity strip */}
            <div className="px-4 py-1.5 bg-[#0033FF] text-white text-[11px] font-semibold flex items-center gap-2 overflow-hidden shrink-0">
              <Robot size={13} weight="fill" className="shrink-0" />
              <AnimatePresence mode="wait">
                <motion.span key={phase} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.3 }} className="flex items-center gap-1.5 truncate">
                  {phase === 0 && <MagnifyingGlass size={12} weight="bold" />}
                  {AP_PHASES[phase]}
                </motion.span>
              </AnimatePresence>
            </div>

            {/* list / detail */}
            <div className="flex-1 overflow-hidden relative">
              <AnimatePresence mode="wait">
                {showList ? (
                  <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }} className="h-full overflow-y-auto scroll-thin p-3 space-y-2.5">
                    {AP_TICKETS.map((tk, i) => {
                      const av = AP_AVATARS[i % AP_AVATARS.length];
                      const isResolved = tk.focus && phase === 5;
                      const scanning = tk.focus && phase === 0;
                      return (
                        <motion.div key={tk.id}
                          animate={scanning ? { boxShadow: ["0 0 0 0 rgba(0,51,255,0)", "0 0 0 2px rgba(0,51,255,0.35)", "0 0 0 0 rgba(0,51,255,0)"] } : {}}
                          transition={scanning ? { duration: 1.6, repeat: Infinity } : {}}
                          className="bg-white border border-zinc-200 rounded-lg px-3 py-3 flex gap-3">
                          <input type="checkbox" disabled readOnly className="h-3.5 w-3.5 rounded border-zinc-300 accent-[#0033FF] mt-1 shrink-0" />
                          <div className="h-9 w-9 rounded-full flex items-center justify-center text-[12px] font-bold shrink-0" style={{ background: av.bg, color: av.fg }}>
                            {tk.cust.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-1.5 mb-1">
                              <FdTag kind="overdue">Förfallen</FdTag>
                              <FdTag kind="replied">Kunden svarade</FdTag>
                              {tk.extra.map((t) => <FdTag key={t} kind="plain">{t}</FdTag>)}
                            </div>
                            <div className={`text-[13px] font-semibold truncate ${isResolved ? "text-zinc-400 line-through" : "text-[#0B1F33]"}`}>{tk.subj} <span className="text-zinc-400 font-normal">#{tk.id}</span></div>
                            <div className="flex items-center gap-1.5 text-[11px] text-zinc-400 mt-1 flex-wrap">
                              <Envelope size={12} weight="fill" className="text-zinc-400" />
                              <span className="text-zinc-500 font-medium">{tk.cust}</span>
                              <span>· Kunden svarade för {tk.replied} sedan</span>
                              <span className="hidden sm:inline">·</span>
                              <span className="hidden sm:flex items-center gap-1"><Clock size={11} weight="bold" /> Lösning är försenad med {tk.overdue}</span>
                            </div>
                          </div>
                          <div className="hidden md:flex flex-col items-end gap-1.5 w-36 shrink-0 text-[11px] text-zinc-500">
                            <span className="flex items-center gap-1.5"><span className="h-3 w-1.5 rounded-sm" style={{ background: AP_PRIO[tk.prio] }} /> {tk.prio} <CaretDown size={9} weight="bold" /></span>
                            <span className="flex items-center gap-1 truncate max-w-full"><User size={12} weight="regular" /> <span className="truncate">{tk.agent}</span> <CaretDown size={9} weight="bold" /></span>
                            {isResolved ? (
                              <span className="flex items-center gap-1 font-bold text-[#28C840]"><CheckCircle size={12} weight="fill" /> Löst</span>
                            ) : (
                              <span className="flex items-center gap-1"><Waveform size={12} weight="bold" className="text-zinc-400" /> Öppen <CaretDown size={9} weight="bold" /></span>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </motion.div>
                ) : (
                  <motion.div key="detail" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.32 }} className="flex flex-col h-full bg-white">
                    {/* ticket header */}
                    <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-zinc-200 shrink-0">
                      <CaretLeft size={14} weight="bold" className="text-zinc-400" />
                      <div className="h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0" style={{ background: AP_AVATARS[0].bg, color: AP_AVATARS[0].fg }}>AL</div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-semibold text-[#0B1F33] truncate">{focus.subj} <span className="text-zinc-400 font-normal">#{focus.id}</span></div>
                        <div className="text-[11px] text-zinc-400">{focus.cust}</div>
                      </div>
                      <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1 ${phase >= 4 ? "bg-[#28C840]/15 text-[#28C840]" : "bg-[#0033FF]/10 text-[#0033FF]"}`}>
                        {phase >= 4 ? <><CheckCircle size={12} weight="fill" /> Löst</> : <><Waveform size={11} weight="bold" /> Öppen</>}
                      </span>
                    </div>

                    {/* conversation */}
                    <div className="flex-1 overflow-y-auto scroll-thin px-4 py-3 space-y-3 bg-[#F5F6F8]">
                      <div className="flex gap-2.5">
                        <div className="h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0" style={{ background: AP_AVATARS[0].bg, color: AP_AVATARS[0].fg }}>AL</div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] text-zinc-400 mb-1"><b className="text-zinc-600">Anna Lindqvist</b> · för 3 dagar sedan</div>
                          <div className="text-[13px] text-zinc-700 bg-white border border-zinc-200 rounded-lg rounded-tl-sm px-3.5 py-2.5">
                            Hej! Jag vill returnera ett par skor som tyvärr var för små. Hur går jag tillväga?
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2.5">
                        <div className="h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0" style={{ background: AP_AVATARS[0].bg, color: AP_AVATARS[0].fg }}>AL</div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] text-zinc-400 mb-1"><b className="text-zinc-600">Anna Lindqvist</b> · för 5 min sedan</div>
                          <div className="text-[13px] text-zinc-700 bg-white border border-zinc-200 rounded-lg rounded-tl-sm px-3.5 py-2.5">
                            Hej igen! Jag skrev för tre dagar sedan men har fortfarande inte fått något svar. Varför har ingen hört av sig? 😟
                          </div>
                        </div>
                      </div>
                      {phase >= 3 && (
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex gap-2.5">
                          <div className="h-7 w-7 rounded-full bg-[#0033FF] flex items-center justify-center shrink-0"><Robot size={13} weight="fill" className="text-white" /></div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[11px] text-zinc-400 mb-1 flex items-center gap-1"><b className="text-[#0033FF]">Oskar - AI</b> · agentsvar <CheckCircle size={12} weight="fill" className="text-[#28C840]" /> Skickat</div>
                            <div className="text-[13px] text-zinc-800 bg-[#0033FF]/[0.06] border border-[#0033FF]/20 rounded-lg rounded-tl-sm px-3.5 py-2.5">
                              Hej Anna! Jag är hemskt ledsen för dröjsmålet — det ska inte hända. Vi löser det direkt: jag har skapat en förbetald returetikett som finns i din mejl. Återbetalningen sker inom 3–5 bankdagar efter att paketet kommit till oss. 📦
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </div>

                    {/* reply editor / actions */}
                    <div className="border-t border-zinc-200 bg-white p-3 shrink-0">
                      {phase < 3 ? (
                        <div className="rounded-lg border border-zinc-200 overflow-hidden">
                          <div className="flex items-center gap-3 px-3 py-1.5 border-b border-zinc-100 text-zinc-300">
                            <span className="text-[11px] font-bold text-zinc-400">Svara</span>
                            <span className="h-3 w-px bg-zinc-200" />
                            <span className="font-bold text-zinc-300 text-xs">B</span>
                            <span className="italic text-zinc-300 text-xs">I</span>
                            <PaperPlaneRight size={12} weight="fill" className="ml-auto text-zinc-300" />
                          </div>
                          <div className="px-3.5 py-2.5 min-h-[42px] text-[13px]">
                            {phase === 2 ? (
                              <span className="inline-flex items-center gap-2 text-zinc-400">
                                <span className="inline-flex gap-1">
                                  <span className="h-1.5 w-1.5 rounded-full bg-[#0033FF] typing-dot" />
                                  <span className="h-1.5 w-1.5 rounded-full bg-[#0033FF] typing-dot" style={{ animationDelay: "0.2s" }} />
                                  <span className="h-1.5 w-1.5 rounded-full bg-[#0033FF] typing-dot" style={{ animationDelay: "0.4s" }} />
                                </span>
                                Oskar - AI skriver ett svar…
                              </span>
                            ) : (
                              <span className="text-zinc-300">Skriv ett svar…</span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] text-zinc-400">Svar genererat av Oskar - AI</span>
                          <motion.div animate={phase === 4 && !resolved ? { scale: [1, 0.92, 1] } : {}} transition={{ duration: 0.5 }}
                            className={`flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-md ${resolved ? "bg-[#28C840]/15 text-[#28C840]" : phase === 4 ? "bg-[#28C840] text-white shadow-lg shadow-[#28C840]/30" : "bg-zinc-100 text-zinc-500"}`}>
                            <CheckCircle size={15} weight="fill" /> {resolved ? "Stängt som löst" : "Stäng som löst"}
                          </motion.div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* filter panel */}
          <div className="w-56 border-l border-zinc-200 bg-white p-4 hidden xl:flex flex-col gap-4 shrink-0 overflow-y-auto scroll-thin">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Filter</span>
              <span className="text-[11px] font-semibold text-[#0033FF]">Visa tillämpade filter</span>
            </div>
            <div className="flex items-center gap-1.5 rounded-md border border-[#0033FF]/40 px-2.5 py-2 text-[11px] text-zinc-400"><MagnifyingGlass size={12} weight="bold" /> Sökfält</div>
            {[["Handläggare", "Vilken handläggare som helst"], ["Grupp", "Vilken grupp som helst"], ["Skapad", "När som helst"], ["Löst den", "När som helst"]].map(([l, p]) => (
              <div key={l} className="space-y-1.5">
                <div className="text-[11px] font-semibold text-zinc-600">{l}</div>
                <div className="flex items-center justify-between rounded-md border border-zinc-200 px-2.5 py-1.5 text-[11px] text-zinc-400">{p} <CaretDown size={11} weight="bold" /></div>
              </div>
            ))}
            <div className="space-y-1.5">
              <div className="text-[11px] font-semibold text-zinc-600">Lösningens förfallotid</div>
              <div className="flex items-center justify-between rounded-md border border-zinc-200 px-2.5 py-1.5 text-[11px] text-zinc-600">
                <span className="flex items-center gap-1 bg-zinc-100 rounded px-1.5 py-0.5 font-semibold">Förfallet ✕</span>
                <CaretDown size={11} weight="bold" className="text-zinc-400" />
              </div>
            </div>
            <button className="mt-auto bg-[#0033FF] text-white text-[12px] font-bold rounded-md py-2.5">Tillämpa</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BrochureModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [sending, setSending] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) { toast.error("Fyll i din e-postadress"); return; }
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/brochure-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error("fail");
      toast.success(data.message || "Tack! Vi hör av oss med broschyren.");
      setForm({ name: "", email: "", company: "" });
      onClose();
    } catch {
      toast.error("Något gick fel. Försök igen om en stund.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="brochure-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, y: 16, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="relative w-full max-w-md rounded-2xl border border-white/10 bg-zinc-950 p-7 shadow-2xl">
        <button data-testid="brochure-modal-close" onClick={onClose}
          className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors">
          <Plus size={22} weight="bold" className="rotate-45" />
        </button>
        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black px-3 py-1 text-xs font-semibold text-[#5C7CFF] mb-4">
          <FileArrowDown size={14} weight="bold" /> Gratis broschyr
        </div>
        <h3 className="font-display font-black text-2xl tracking-tight">Få Senzum AI-broschyren</h3>
        <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
          Fyll i dina uppgifter så skickar vi broschyren direkt till din inkorg.
        </p>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <input data-testid="brochure-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Namn" autoFocus
            className="w-full bg-black border border-white/10 rounded-md px-4 py-2.5 text-sm text-white placeholder-zinc-500 focus:ring-2 focus:ring-[#0033FF]/40 focus:border-[#0033FF] outline-none transition-all" />
          <input data-testid="brochure-email" type="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="E-postadress *"
            className="w-full bg-black border border-white/10 rounded-md px-4 py-2.5 text-sm text-white placeholder-zinc-500 focus:ring-2 focus:ring-[#0033FF]/40 focus:border-[#0033FF] outline-none transition-all" />
          <input data-testid="brochure-company" value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
            placeholder="Företag (valfritt)"
            className="w-full bg-black border border-white/10 rounded-md px-4 py-2.5 text-sm text-white placeholder-zinc-500 focus:ring-2 focus:ring-[#0033FF]/40 focus:border-[#0033FF] outline-none transition-all" />
          <button data-testid="brochure-submit" type="submit" disabled={sending}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-6 py-3 font-semibold transition-colors disabled:opacity-60">
            {sending ? "Skickar…" : <>Skicka till mig <PaperPlaneRight size={16} weight="bold" /></>}
          </button>
        </form>
        <p className="mt-3 text-[11px] text-zinc-600 text-center">Vi använder dina uppgifter enbart för att skicka broschyren och eventuell uppföljning.</p>
      </motion.div>
    </div>
  );
}

function ContactModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.message.trim()) { toast.error("Fyll i e-post och meddelande"); return; }
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("fail");
      toast.success("Tack! Vi återkommer så snart vi kan.");
      setForm({ name: "", email: "", company: "", message: "" });
      onClose();
    } catch {
      toast.error("Något gick fel. Försök igen om en stund.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="contact-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, y: 16, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="relative w-full max-w-md rounded-2xl border border-white/10 bg-zinc-950 p-7 shadow-2xl">
        <button data-testid="contact-modal-close" onClick={onClose}
          className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors">
          <Plus size={22} weight="bold" className="rotate-45" />
        </button>
        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black px-3 py-1 text-xs font-semibold text-[#5C7CFF] mb-4">
          <Sparkle size={14} weight="bold" /> Kontakta oss
        </div>
        <h3 className="font-display font-black text-2xl tracking-tight">Hör av dig till oss</h3>
        <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
          Fyll i formuläret så återkommer vi så snart vi kan.
        </p>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <input data-testid="contact-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Namn" autoFocus
            className="w-full bg-black border border-white/10 rounded-md px-4 py-2.5 text-sm text-white placeholder-zinc-500 focus:ring-2 focus:ring-[#0033FF]/40 focus:border-[#0033FF] outline-none transition-all" />
          <input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="E-postadress *"
            className="w-full bg-black border border-white/10 rounded-md px-4 py-2.5 text-sm text-white placeholder-zinc-500 focus:ring-2 focus:ring-[#0033FF]/40 focus:border-[#0033FF] outline-none transition-all" />
          <input data-testid="contact-company" value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
            placeholder="Företag (valfritt)"
            className="w-full bg-black border border-white/10 rounded-md px-4 py-2.5 text-sm text-white placeholder-zinc-500 focus:ring-2 focus:ring-[#0033FF]/40 focus:border-[#0033FF] outline-none transition-all" />
          <textarea data-testid="contact-message" required rows={4} value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            placeholder="Ditt meddelande *"
            className="w-full bg-black border border-white/10 rounded-md px-4 py-2.5 text-sm text-white placeholder-zinc-500 focus:ring-2 focus:ring-[#0033FF]/40 focus:border-[#0033FF] outline-none transition-all resize-none" />
          <button data-testid="contact-submit" type="submit" disabled={sending}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-6 py-3 font-semibold transition-colors disabled:opacity-60">
            {sending ? "Skickar…" : <>Skicka meddelande <PaperPlaneRight size={16} weight="bold" /></>}
          </button>
        </form>
        <p className="mt-3 text-[11px] text-zinc-600 text-center">Vi använder dina uppgifter enbart för att besvara din förfrågan.</p>
      </motion.div>
    </div>
  );
}

export default function Landing() {
  const [showBrochure, setShowBrochure] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  return (
    <div className="min-h-screen bg-[#060610] text-white overflow-x-hidden">
      <Seo title="Senzum AI – AI-driven kundservice för chatt & telefon" description="Senzum AI låter dig bygga anpassningsbara AI-kundtjänstagenter för chatt och telefon: högkvalitativa svar, handsfree röst, automatisk ärendehantering, kunskapsbas och samtalsanalys." canonical="https://senzumai.com/" />
      <IntroVideo />
      <BrochureModal open={showBrochure} onClose={() => setShowBrochure(false)} />
      <ContactModal open={showContact} onClose={() => setShowContact(false)} />
      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-gradient-to-b from-[#0B1235]/85 to-[#060610]/65 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-white">
            <a href="#features" className="hover:text-white/70 transition-colors">Plattform</a>
            <a href="#channels" className="hover:text-white/70 transition-colors">Kanaler</a>
            <a href="#compliance" className="hover:text-white/70 transition-colors">Säkerhet & EU</a>
            <button data-testid="nav-contact-link" onClick={() => setShowContact(true)} className="hover:text-white/70 transition-colors">Kontakta oss</button>
          </nav>
          <div className="flex items-center gap-2 md:gap-3">
            <Link data-testid="nav-agent-login-link" to="/login?next=/agent" className="hidden md:inline-flex items-center gap-1.5 text-sm font-semibold text-white hover:text-white/70 px-3 py-2">
              <ChatCircleDots size={15} weight="fill" /> Agentläge
            </Link>
            <Link data-testid="nav-login-link" to="/login" className="hidden md:inline-flex text-sm font-semibold text-white hover:text-white/70 px-3 py-2">
              Logga in
            </Link>
            <button
              data-testid="mobile-menu-button"
              aria-label="Öppna meny"
              onClick={() => setMobileNav((v) => !v)}
              className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-md text-zinc-300 hover:text-white hover:bg-white/10 transition-colors"
            >
              {mobileNav ? <X size={22} weight="bold" /> : <List size={22} weight="bold" />}
            </button>
          </div>
        </div>
        {mobileNav && (
          <div data-testid="mobile-menu" className="md:hidden border-t border-white/5 bg-gradient-to-b from-[#0B1235]/95 to-[#060610]/95 backdrop-blur-xl">
            <nav className="px-6 py-3 flex flex-col text-sm font-medium text-zinc-300">
              <a href="#features" onClick={() => setMobileNav(false)} className="py-3 border-b border-white/5 hover:text-white transition-colors">Plattform</a>
              <a href="#channels" onClick={() => setMobileNav(false)} className="py-3 border-b border-white/5 hover:text-white transition-colors">Kanaler</a>
              <a href="#compliance" onClick={() => setMobileNav(false)} className="py-3 border-b border-white/5 hover:text-white transition-colors">Säkerhet & EU</a>
              <button data-testid="mobile-contact-link" onClick={() => { setMobileNav(false); setShowContact(true); }} className="py-3 border-b border-white/5 hover:text-white transition-colors text-left">Kontakta oss</button>
              <Link to="/login" onClick={() => setMobileNav(false)} className="py-3 hover:text-white transition-colors">Logga in</Link>
              <Link to="/login?next=/agent" onClick={() => setMobileNav(false)} className="py-3 hover:text-white transition-colors flex items-center gap-2"><ChatCircleDots size={16} weight="fill" /> Agentläge</Link>
            </nav>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="absolute -top-32 right-0 h-[480px] w-[480px] rounded-full bg-[#0033FF]/8 blur-[120px]" />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[420px] bg-[radial-gradient(ellipse_at_top,_rgba(0,51,255,0.14),_transparent_70%)] blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-8 pb-16 lg:pt-12 lg:pb-20 flex flex-col items-center text-center">
          <motion.div initial="hidden" animate="show" variants={fadeUp}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-zinc-950 px-3 py-1 text-xs font-semibold text-zinc-400 mb-4">
            <Sparkle size={14} weight="fill" className="text-[#5C7CFF]" />
            Bygg · Anpassa · Lansera unika AI-agenter som blir smartare med tiden
          </motion.div>
          <motion.h1 custom={1} initial="hidden" animate="show" variants={fadeUp}
            className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter max-w-5xl mx-auto">
            AI-lösningar för kundservice inom
            <span className="text-[#5C7CFF]"> chatt, röst & analys</span>
            <span className="block">sömlöst integrerade i er verksamhet.</span>
          </motion.h1>
          <motion.p custom={2} initial="hidden" animate="show" variants={fadeUp}
            className="mt-5 text-base text-zinc-400 max-w-2xl mx-auto leading-relaxed">
            Senzum AI utvecklar och driftsätter skräddarsydda AI-agenter för företag, anpassade efter er
            verksamhet, era processer och er kunskap. Varje AI-agent byggs med ert varumärke, integreras med
            era system och kan sömlöst lämna över till en människa när det behövs.
          </motion.p>
          <motion.div custom={3} initial="hidden" animate="show" variants={fadeUp}
            className="mt-6 flex justify-center">
            <button data-testid="hero-cta-brochure" onClick={() => setShowBrochure(true)}
              className="inline-flex items-center justify-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-7 py-3.5 font-semibold transition-colors shadow-lg shadow-[#0033FF]/20 hover:shadow-[#0033FF]/30">
              <FileArrowDown size={18} weight="bold" /> Vill du veta mer? Få vår broschyr
            </button>
          </motion.div>

          {/* Floating product preview */}
          <motion.div custom={4} initial="hidden" animate="show" variants={fadeUp}
            className="mt-8 w-full grid md:grid-cols-2 gap-5 max-w-5xl mx-auto text-left">
            <div className="rounded-xl border border-white/10 bg-zinc-950 p-5 shadow-sm">
              <div className="flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-zinc-400 mb-4">
                <ChatCircleDots size={16} weight="bold" className="text-[#5C7CFF]" /> AI-Agent
              </div>
              <AnimatedChatPreview />
            </div>
            <VoiceShowcase />
          </motion.div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-transparent overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex items-center">
          <div className="relative flex-1 overflow-hidden marquee-pause [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]">
            <div className="flex w-max animate-marquee">
              {[...BRANDS, ...BRANDS].map((b, i) => (
                <span key={i} className="flex items-center gap-2 text-zinc-500 text-sm font-semibold whitespace-nowrap mr-12">
                  {b.icon} {b.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Autopilot simulation */}
      <section id="autopilot" className="bg-transparent text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_15%_25%,rgba(0,51,255,0.13),transparent_60%)] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 sm:py-32">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C7CFF] mb-4">Autopilot</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Din AI löser ärenden <span className="text-[#5C7CFF]">medan du sover.</span>
            </h2>
            <p className="mt-5 text-zinc-400 text-lg leading-relaxed">
              Autopilot bevakar din Freshdesk-inkorg, utformar svar från din kunskapsbas, skickar dem till kunden
              och stänger ärenden automatiskt — dygnet runt.
            </p>
          </div>
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} className="mt-12 max-w-5xl mx-auto">
            <FreshdeskAutopilot />
          </motion.div>
        </div>
      </section>

      {/* Features bento */}
      <section id="features" className="relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-screen h-full bg-[radial-gradient(ellipse_55%_50%_at_50%_45%,rgba(0,51,255,0.08),transparent_60%)] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 sm:py-32">
        <div className="max-w-2xl mx-auto text-center">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C7CFF] mb-4">Plattformen</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
            Allt du behöver för att automatisera support.
          </h2>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {[
            { icon: Brain, title: "Skräddarsydd AI-hjärna", desc: "Finjustera persona, ton, regler och beteende per agent — så att varje svar låter precis som ditt varumärke. Ingen teknisk kunskap krävs." },
            { icon: Books, title: "Tränad på din kunskap", desc: "Lägg till dokument, frågor & svar och länkar. Agenterna svarar enbart utifrån din kunskapsbas — inga påhitt." },
            { icon: Lightning, title: "Lansera på minuter", desc: "Bygg, testa i lekstugan och gå live med en inbäddningsbar widget eller en röstlinje i webbläsaren." },
            { icon: Headset, title: "Skapad för kundservice", desc: "Byggd av kundservicemänniskor för kundservice — med smidig eskalering till en människa exakt när det behövs." },
            { icon: Plugs, title: "Helpdesk-integrationer", desc: "Skicka inkommande Freshdesk- och Zendesk-ärenden direkt till dina AI-agenter för snabba förstasvar." },
            { icon: Sparkle, title: "Unik AI som blir smartare med tiden", desc: "Den lär sig av varje interaktion den har — av både positiv och negativ feedback — och förbättras automatiskt över tid." },
          ].map((f, i) => (
            <motion.div key={i} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
              className="rounded-xl border border-white/10 bg-zinc-950 p-7 hover:-translate-y-1 transition-transform duration-300">
              <div className="h-11 w-11 rounded-lg bg-[#0033FF]/10 flex items-center justify-center mb-5">
                <f.icon size={22} weight="duotone" className="text-[#5C7CFF]" />
              </div>
              <h3 className="font-display font-bold text-xl mb-2">{f.title}</h3>
              <p className="text-zinc-400 leading-relaxed text-[15px]">{f.desc}</p>
            </motion.div>
          ))}
        </div>
        </div>
      </section>

      {/* Channels dark section */}
      <section id="channels" className="bg-transparent text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_85%_45%,rgba(0,51,255,0.12),transparent_60%)] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 sm:py-32 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C7CFF] mb-4">Två kanaler, en hjärna</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
              Samma kunskapsbas driver chatt <span className="text-[#5C7CFF]">och</span> röst.
            </h2>
            <p className="mt-6 text-zinc-400 text-lg leading-relaxed">
              Designa en gång, driftsätt överallt. Din AI-agent hanterar webbchatt och svarar på telefonsamtal
              med identiska instruktioner och kunskap — och lämnar över till en människa exakt när du vill.
            </p>
            <div className="mt-8 space-y-4">
              {[
                ["Chattwidget", "Bädda in på vilken sida som helst med ett kodavsnitt."],
                ["Webbläsarröst", "Testa riktiga talade konversationer direkt i din dashboard."],
                ["Telefoniklart", "Byggt för att anslutas till Zisson, Genesys och fler."],
              ].map(([t, d]) => (
                <div key={t} className="flex gap-3">
                  <div className="h-6 w-6 rounded-full bg-[#0033FF]/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Star size={12} weight="fill" className="text-[#5C7CFF]" />
                  </div>
                  <div>
                    <div className="font-semibold">{t}</div>
                    <div className="text-zinc-400 text-sm">{d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <AnimatedConversation />
        </div>
      </section>

      {/* EU AI Act & GDPR trust section */}
      <section id="compliance" className="bg-transparent text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_65%_at_25%_60%,rgba(0,51,255,0.11),transparent_62%)] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 sm:py-32">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#0033FF]/40 bg-[#0033FF]/10 text-[#5B7CFF] px-4 py-1.5 text-sm font-semibold">
              <GlobeHemisphereWest size={16} weight="fill" /> EU-datalagring · Byggt för europeisk regelefterlevnad
            </span>
            <h2 className="mt-6 font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Förtroende och regelefterlevnad — inbyggt från grunden.
            </h2>
            <p className="mt-5 text-zinc-400 text-lg">
              Senzum AI är byggt för att möta EU:s AI-förordning (AI Act) och GDPR. Era kunders data
              stannar inom EU, och varje agent är transparent, spårbar och under er kontroll.
            </p>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Eye, title: "AI-transparens", text: "Kunder informeras tydligt om att de chattar med en AI — enligt AI Actens transparensplikt." },
              { icon: ChatCircleDots, title: "Mänsklig överlämning", text: "Kunden kan alltid be att få prata med en människa. Ingen inlåsning i automatiserade beslut." },
              { icon: Lock, title: "PII-maskering", text: "Känsliga personuppgifter maskeras automatiskt innan de behandlas av modellen." },
              { icon: ListChecks, title: "Spårbarhet & audit-logg", text: "Alla administrativa åtgärder loggas med tidsstämpel och användare — full revisionsspårbarhet." },
              { icon: Clock, title: "Datagallring", text: "Konfigurerbar automatisk radering av konversationsdata efter den period ni väljer." },
              { icon: Scales, title: "Rätt att bli raderad (GDPR)", text: "Exportera eller radera en kunds samtliga data på begäran — med ett klick." },
            ].map((c) => (
              <div key={c.title} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:border-[#0033FF]/40 hover:bg-white/[0.05] transition-colors">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl bg-[#0033FF]/15 flex items-center justify-center"><c.icon size={22} weight="bold" className="text-[#5B7CFF]" /></div>
                  <h3 className="font-display font-bold text-lg flex items-center gap-1.5">{c.title} <CheckCircle size={16} weight="fill" className="text-[#5B7CFF]" /></h3>
                </div>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{c.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
            {[
              { icon: GlobeHemisphereWest, label: "EU-datalagring" },
              { icon: Scales, label: "EU AI Act-redo" },
              { icon: ShieldCheck, label: "GDPR-efterlevnad" },
            ].map((b) => (
              <div key={b.label} className="inline-flex items-center gap-2 rounded-full border border-[#0033FF]/30 bg-[#0033FF]/10 text-[#5B7CFF] px-4 py-2 text-sm font-semibold">
                <b.icon size={17} weight="fill" /> {b.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="relative overflow-hidden bg-gradient-to-b from-transparent to-[#0B1235]/30 border-t border-white/5">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[700px] h-[260px] bg-[radial-gradient(ellipse_at_bottom,rgba(0,51,255,0.10),transparent_70%)] blur-2xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo />
          <p className="text-sm text-zinc-500">© 2026 Senzum AI. Premiumplattform för AI-kundtjänst.</p>
        </div>
      </footer>
    </div>
  );
}
